import { fuzzyMatch, substringMatch } from 'features/libra/helpers/fuzzyMatch'

export type MatchTier = 'exact' | 'substring' | 'fuzzy'

export type TermMatchStatus =
  | 'matched'
  | 'ambiguous'
  | 'duplicate'
  | 'unmatched'

export interface TermMatch {
  term: string
  status: TermMatchStatus
  tier?: MatchTier
  /** Values this term contributes to the filter. Only set when matched. */
  resolvedValues: ReadonlyArray<string>
  /** Values to choose between. Only set when ambiguous. */
  candidateValues: ReadonlyArray<string>
  /** Index of the term that already claimed this value. Only set when duplicate. */
  duplicateOfTermIndex?: number
}

export type TermMatchCounts = Record<TermMatchStatus, number>

export interface TermMatchReport {
  matches: ReadonlyArray<TermMatch>
  selectedValues: ReadonlyArray<string>
  counts: TermMatchCounts
}

export interface TermMatchInput {
  terms: ReadonlyArray<string>
  fieldValues: ReadonlyArray<string>
  chosenValuesByTerm: Readonly<Record<string, ReadonlyArray<string>>>
  shouldSuggestFuzzyMatches: boolean
}

const TERM_SEPARATORS = /[\n\r\t;]+/
const MAX_TERMS = 5_000

export function normalizeTerm(term: string): string {
  return term.trim().replace(/\s+/g, ' ').toUpperCase()
}

function stripWrappingQuotes(term: string): string {
  return term.replace(/^["']|["']$/g, '')
}

function splitSegmentIntoTerms(
  segment: string,
  knownNormalizedValues: ReadonlySet<string>
): ReadonlyArray<string> {
  const term = stripWrappingQuotes(segment.trim())
  if (!term) {
    return []
  }
  // A segment is only split on commas when it isn't a value itself, so
  // "Pump, Feedwater A" stays whole while "A-101, A-102" becomes two terms.
  if (!term.includes(',') || knownNormalizedValues.has(normalizeTerm(term))) {
    return [term]
  }
  return term
    .split(',')
    .map((part) => stripWrappingQuotes(part.trim()))
    .filter((part) => part.length > 0)
}

/** Splits pasted text (an Excel column, or a comma-separated list) into terms. */
export function parsePastedTerms(
  rawText: string,
  knownNormalizedValues: ReadonlySet<string>
): ReadonlyArray<string> {
  return rawText
    .split(TERM_SEPARATORS)
    .flatMap((segment) => splitSegmentIntoTerms(segment, knownNormalizedValues))
    .slice(0, MAX_TERMS)
}

function findCandidateValues(
  term: string,
  fieldValues: ReadonlyArray<string>,
  shouldSuggestFuzzyMatches: boolean
): { tier?: MatchTier; candidateValues: ReadonlyArray<string> } {
  const normalizedTerm = normalizeTerm(term)
  const exactValues = fieldValues.filter(
    (value) => normalizeTerm(value) === normalizedTerm
  )
  if (exactValues.length) {
    return { tier: 'exact', candidateValues: exactValues }
  }

  const substringValues = fieldValues.filter(
    (value) => substringMatch(value, term) || substringMatch(term, value)
  )
  if (substringValues.length) {
    return { tier: 'substring', candidateValues: substringValues }
  }

  if (!shouldSuggestFuzzyMatches) {
    return { candidateValues: [] }
  }
  // fuzzyMatch lowercases the text but not the query.
  const fuzzyValues = fieldValues.filter((value) =>
    fuzzyMatch(value, term.trim().toLowerCase())
  )
  return { tier: 'fuzzy', candidateValues: fuzzyValues }
}

function countByStatus(matches: ReadonlyArray<TermMatch>): TermMatchCounts {
  return matches.reduce<TermMatchCounts>(
    (counts, match) => ({
      ...counts,
      [match.status]: counts[match.status] + 1,
    }),
    { matched: 0, ambiguous: 0, duplicate: 0, unmatched: 0 }
  )
}

/**
 * Resolves each pasted term to the field values it should filter on.
 *
 * A term matching several values contributes nothing until the user picks,
 * so the applied filter is always exactly the values shown as matched.
 */
export function matchTermsToValues({
  terms,
  fieldValues,
  chosenValuesByTerm,
  shouldSuggestFuzzyMatches,
}: TermMatchInput): TermMatchReport {
  const claimingTermIndexByValue = new Map<string, number>()
  const matches: TermMatch[] = []

  terms.forEach((term, termIndex) => {
    const chosenValues = chosenValuesByTerm[normalizeTerm(term)] ?? []
    const { tier, candidateValues } = chosenValues.length
      ? { tier: undefined, candidateValues: chosenValues }
      : findCandidateValues(term, fieldValues, shouldSuggestFuzzyMatches)

    if (!candidateValues.length) {
      matches.push({
        term,
        status: 'unmatched',
        resolvedValues: [],
        candidateValues: [],
      })
      return
    }

    const needsChoice =
      !chosenValues.length && (candidateValues.length > 1 || tier === 'fuzzy')
    if (needsChoice) {
      matches.push({
        term,
        status: 'ambiguous',
        tier,
        resolvedValues: [],
        candidateValues,
      })
      return
    }

    const unclaimedValues = candidateValues.filter(
      (value) => !claimingTermIndexByValue.has(value)
    )
    if (!unclaimedValues.length) {
      matches.push({
        term,
        status: 'duplicate',
        tier,
        resolvedValues: [],
        candidateValues: [],
        duplicateOfTermIndex: claimingTermIndexByValue.get(candidateValues[0]),
      })
      return
    }

    unclaimedValues.forEach((value) =>
      claimingTermIndexByValue.set(value, termIndex)
    )
    matches.push({
      term,
      status: 'matched',
      tier,
      resolvedValues: unclaimedValues,
      candidateValues: [],
    })
  })

  return {
    matches,
    selectedValues: [...claimingTermIndexByValue.keys()],
    counts: countByStatus(matches),
  }
}
